import { FC, useMemo, useState } from 'react'
import pluralize from 'pluralize'
import { Box, Button, CircularProgress, Grid, InputAdornment, Tab, Tabs, TextField, Typography } from '@mui/material'
import { DataGridPremium, GridCellParams, useGridApiRef } from '@mui/x-data-grid-premium'
import { DateRange } from '@mui/x-date-pickers-pro'
import { startOfDay, endOfDay, formatISO } from 'date-fns'
import { ConfigurationParameterTypeEnum } from 'src/api'
import { useConfigurationListRequest, useSensorOrientationReadout } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'
import { classifyMovementSeverity, detectSensorMovements, movementSeverityBands, MovementDetectionResult } from 'src/helpers'
import { DateRangePresetPicker, getDateRangePresetValue } from 'src/components/ui'
import { SensorMovementDetectionColumns, severityPalette } from 'src/components/columns/sensorMovementDetection'
import { SensorLocationMovementPanel } from './sensorLocationMovementPanel'

interface Props {
  sensorUniqueId: string
}

const getColumns = () => [
  SensorMovementDetectionColumns.Time,
  SensorMovementDetectionColumns.DeltaX,
  SensorMovementDetectionColumns.DeltaY,
  SensorMovementDetectionColumns.DeltaTiltZ,
  SensorMovementDetectionColumns.Severity
]

const severityCellStyles: Record<string, { backgroundColor: string; color: string }> = Object.fromEntries(
  Object.entries(severityPalette).map(([severity, colors]) => [`& .MuiDataGrid-cell--severity-${severity}`, { backgroundColor: colors.background, color: colors.text }])
)

const ResultsSummary: FC<{ count: number }> = ({ count }) => (
  <Grid container spacing={2} alignItems="center" mb={1}>
    <Grid item>
      <Typography variant="caption" color="text.secondary">
        {pluralize('movement', count, true)} found
      </Typography>
    </Grid>
    {movementSeverityBands.map((band) => (
      <Grid item key={band.severity} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: severityPalette[band.severity].background, border: '1px solid', borderColor: severityPalette[band.severity].text }} />
        <Typography variant="caption" sx={{ color: severityPalette[band.severity].text }}>
          {band.label} {band.from === 0 ? `<${band.to}°` : `${band.from}–${band.to}°`} · {band.description}
        </Typography>
      </Grid>
    ))}
  </Grid>
)

const SensorOrientationMovementPanel: FC<Props> = ({ sensorUniqueId }) => {
  const apiRef = useGridApiRef()
  const addPopupMessage = useAddPopupMessage()
  const [dateRange, setDateRange] = useState<DateRange<Date>>(getDateRangePresetValue('thisWeek'))
  const [dateFrom, dateTo] = dateRange
  // undefined = untouched by the user, fall back to the configured default
  const [thresholdOverride, setThresholdOverride] = useState<number | '' | undefined>(undefined)

  const { data: configurationListData } = useConfigurationListRequest({ params: { limit: 50 } })
  const defaultThreshold = useMemo(() => {
    return Number(configurationListData?.find((param) => param.type === ConfigurationParameterTypeEnum.GatOrientationChangeThreshold)?.value || 5)
  }, [configurationListData])
  const threshold = thresholdOverride === undefined ? defaultThreshold : thresholdOverride

  const readoutRequest = useSensorOrientationReadout({
    sensorUniqueId,
    options: {
      onError: ({ message: text }) => addPopupMessage({ text, type: 'error' })
    }
  })

  const rows: MovementDetectionResult[] = useMemo(() => detectSensorMovements(readoutRequest.data || [], Number(threshold) || 0), [readoutRequest.data, threshold])

  const canCheck = Boolean(dateFrom && dateTo && dateFrom <= dateTo && Number(threshold) > 0)

  const handleCheck = () => {
    if (!dateFrom || !dateTo) return
    readoutRequest.mutate({
      timeFrom: formatISO(startOfDay(dateFrom)),
      timeTo: formatISO(endOfDay(dateTo))
    })
  }

  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-end" mb={2}>
        <Grid item>
          <DateRangePresetPicker value={dateRange} onChange={setDateRange} initialPreset="thisWeek" />
        </Grid>
        <Grid item>
          <TextField
            label="Threshold"
            type="number"
            value={threshold}
            onChange={({ target: { value } }) => setThresholdOverride(value === '' ? '' : Number(value))}
            InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
            inputProps={{ min: 0, step: 0.1 }}
            sx={{ width: 110 }}
          />
        </Grid>
        <Grid item>
          <Button variant="contained" size="large" onClick={handleCheck} sx={{ minWidth: 180 }} disabled={readoutRequest.isLoading || !canCheck}>
            {readoutRequest.isLoading ? <CircularProgress size={26} color="inherit" /> : 'Check movement'}
          </Button>
        </Grid>
        <Grid item>
          <Button variant="text" size="large" onClick={() => readoutRequest.reset()} disabled={!rows.length}>
            Hide
          </Button>
        </Grid>
      </Grid>
      {readoutRequest.isSuccess && !rows.length ? (
        <Typography variant="body2" color="text.secondary">
          No movement detected in the selected period
        </Typography>
      ) : null}
      {rows.length ? (
        <>
          <ResultsSummary count={rows.length} />
          <DataGridPremium<MovementDetectionResult>
            apiRef={apiRef}
            autoHeight
            disableRowSelectionOnClick
            rows={rows}
            columns={getColumns()}
            loading={readoutRequest.isLoading}
            getRowId={(row) => row.id}
            rowHeight={64}
            hideFooter={rows.length <= 100}
            rowThreshold={2}
            columnThreshold={2}
            getCellClassName={({ field, row }: GridCellParams<MovementDetectionResult>) => {
              if (field === 'deltaX' && row.exceededAxes.includes('motionInX')) return `MuiDataGrid-cell--severity-${classifyMovementSeverity(row.deltaX)}`
              if (field === 'deltaY' && row.exceededAxes.includes('motionInY')) return `MuiDataGrid-cell--severity-${classifyMovementSeverity(row.deltaY)}`
              if (field === 'deltaTiltZ' && row.exceededAxes.includes('tiltZ')) return `MuiDataGrid-cell--severity-${classifyMovementSeverity(row.deltaTiltZ)}`
              return ''
            }}
            localeText={{
              columnMenuSortAsc: 'Sort A-Z',
              columnMenuSortDesc: 'Sort Z-A'
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                paddingLeft: '5px',
                overflow: 'visible'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1,
                fontSize: '13px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.6)',
                overflow: 'visible'
              },
              ...severityCellStyles
            }}
          />
        </>
      ) : null}
    </Box>
  )
}

export const SensorMovementDetection: FC<Props> = ({ sensorUniqueId }) => {
  const [tab, setTab] = useState(0)

  return (
    <Box>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Orientation" />
        <Tab label="Cellular" />
        <Tab label="GPS" />
      </Tabs>
      <Box sx={{ display: tab === 0 ? 'block' : 'none', pt: 2 }}>
        <SensorOrientationMovementPanel sensorUniqueId={sensorUniqueId} />
      </Box>
      <Box sx={{ display: tab === 1 ? 'block' : 'none', pt: 2 }}>
        <SensorLocationMovementPanel sensorUniqueId={sensorUniqueId} locationType="cellularLocation" />
      </Box>
      <Box sx={{ display: tab === 2 ? 'block' : 'none', pt: 2 }}>
        <SensorLocationMovementPanel sensorUniqueId={sensorUniqueId} locationType="gpsLocation" />
      </Box>
    </Box>
  )
}
